import React, { useState } from 'react';
import { DollarSignIcon, LayersIcon, UserIcon, UsersIcon, BuildingIcon, HomeIcon, TagIcon, GitBranchIcon, ArrowUpRightIcon, AlertTriangleIcon, ClockIcon, EyeIcon, CheckCircleIcon, LockIcon, CalendarIcon, PercentIcon, HelpCircleIcon, PlusIcon, TrashIcon, ChevronDownIcon, ChevronUpIcon, SaveIcon } from 'lucide-react';
import { StatusBadge } from '../../StatusBadge';
type FDOAMode = 'Override' | 'Satisfy' | 'CostCentreOnly';
type FDOARuleProps = {
  rule: any;
  onSave: (rule: any) => void;
  onCancel: () => void;
  locale: string;
};
export function FDOARuleEditor({
  rule,
  onSave,
  onCancel,
  locale
}: FDOARuleProps) {
  // Initialize state with rule data or defaults
  const [ruleData, setRuleData] = useState({
    ...rule,
    modes: rule.modes || {
      vendorMode: 'Satisfy' as FDOAMode,
      siteMode: 'Satisfy' as FDOAMode,
      entityMode: 'Override' as FDOAMode
    },
    delegation: rule.delegation || {
      inheritUpperLimit: true,
      useOriginalChain: true,
      maxHops: 5
    },
    fourEyes: rule.fourEyes || {
      enabled: true,
      threshold: 10000,
      applyAt: 'Both',
      currency: 'AUD'
    },
    emergency: rule.emergency || {
      enabled: false,
      approvers: []
    },
    lineLevel: rule.lineLevel || {
      enabled: true,
      evaluationMode: 'Sequential',
      categoriesOnly: true
    },
    preApproval: rule.preApproval || {
      checkClosedPeriods: true
    },
    multiCurrency: rule.multiCurrency || {
      enabled: true,
      baseCurrency: 'AUD',
      evaluateAt: 'Header'
    },
    audit: rule.audit || {
      enabled: true,
      storeDecisions: true
    }
  });
  // Sample data for dropdowns
  const [entities] = useState([{
    id: 'entity-001',
    name: 'Global Technologies Inc.'
  }, {
    id: 'entity-002',
    name: 'European Operations Ltd'
  }, {
    id: 'entity-003',
    name: 'APAC Subsidiary'
  }]);
  const [users] = useState([{
    id: 'user-001',
    name: 'John Smith'
  }, {
    id: 'user-002',
    name: 'Jane Doe'
  }, {
    id: 'user-003',
    name: 'Michael Johnson'
  }, {
    id: 'user-004',
    name: 'Sarah Williams'
  }]);
  const [roles] = useState([{
    id: 'role-001',
    name: 'Finance Manager'
  }, {
    id: 'role-002',
    name: 'Department Head'
  }, {
    id: 'role-003',
    name: 'CEO'
  }, {
    id: 'role-004',
    name: 'CFO'
  }]);
  const [glCategories] = useState([{
    id: 'cat-001',
    code: '1000',
    description: 'IT Equipment',
    entityId: 'entity-001'
  }, {
    id: 'cat-002',
    code: '2000',
    description: 'Office Supplies',
    entityId: 'entity-001'
  }, {
    id: 'cat-003',
    code: '3000',
    description: 'Professional Services',
    entityId: 'entity-001'
  }, {
    id: 'cat-004',
    code: '4000',
    description: 'Travel Expenses',
    entityId: 'entity-001'
  }]);
  const [currencies] = useState([{
    code: 'AUD',
    name: 'Australian Dollar'
  }, {
    code: 'USD',
    name: 'US Dollar'
  }, {
    code: 'EUR',
    name: 'Euro'
  }, {
    code: 'GBP',
    name: 'British Pound'
  }]);
  // UI state
  const [expandedSections, setExpandedSections] = useState({
    defaults: true,
    modes: true,
    delegation: true,
    categories: true,
    emergency: false,
    fourEyes: true,
    lineLevel: false,
    periods: false,
    multiCurrency: false,
    audit: false
  });
  const [defaultApprovers, setDefaultApprovers] = useState(rule.defaultApprovers || [{
    id: 'default-001',
    scopeType: 'Entity',
    scopeId: 'entity-001',
    userOrRoleId: 'role-001',
    isReviewer: false,
    isApprover: true,
    fdoaLimit: 10000,
    currency: 'AUD',
    belongsToCategories: ['cat-001', 'cat-002']
  }, {
    id: 'default-002',
    scopeType: 'Vendor',
    scopeId: 'vendor-001',
    userOrRoleId: 'user-002',
    isReviewer: true,
    isApprover: false,
    fdoaLimit: 0,
    currency: 'AUD',
    belongsToCategories: []
  }]);
  const [categoryApprovers, setCategoryApprovers] = useState(rule.categoryApprovers || [{
    id: 'catapp-001',
    categoryId: 'cat-001',
    sequence: 1,
    userOrRoleId: 'user-001',
    limit: 5000,
    currency: 'AUD'
  }, {
    id: 'catapp-002',
    categoryId: 'cat-001',
    sequence: 2,
    userOrRoleId: 'role-001',
    limit: 25000,
    currency: 'AUD'
  }, {
    id: 'catapp-003',
    categoryId: 'cat-001',
    sequence: 3,
    userOrRoleId: 'role-003',
    limit: 100000,
    currency: 'AUD'
  }]);
  const [accountingPeriods, setAccountingPeriods] = useState(rule.accountingPeriods || [{
    id: 'period-001',
    entityId: 'entity-001',
    periodCode: 'FY23-Q3',
    startUtc: '2023-07-01T00:00:00Z',
    endUtc: '2023-09-30T23:59:59Z',
    isClosed: true
  }, {
    id: 'period-002',
    entityId: 'entity-001',
    periodCode: 'FY23-Q4',
    startUtc: '2023-10-01T00:00:00Z',
    endUtc: '2023-12-31T23:59:59Z',
    isClosed: false
  }]);
  // Helper functions
  const toggleSection = (section: string) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };
  const handleModeChange = (scope: 'vendorMode' | 'siteMode' | 'entityMode', mode: FDOAMode) => {
    setRuleData({
      ...ruleData,
      modes: {
        ...ruleData.modes,
        [scope]: mode
      }
    });
  };
  const handleDelegationChange = (field: string, value: any) => {
    setRuleData({
      ...ruleData,
      delegation: {
        ...ruleData.delegation,
        [field]: value
      }
    });
  };
  const handleFourEyesChange = (field: string, value: any) => {
    setRuleData({
      ...ruleData,
      fourEyes: {
        ...ruleData.fourEyes,
        [field]: value
      }
    });
  };
  const handleEmergencyChange = (field: string, value: any) => {
    setRuleData({
      ...ruleData,
      emergency: {
        ...ruleData.emergency,
        [field]: value
      }
    });
  };
  const handleLineLevelChange = (field: string, value: any) => {
    setRuleData({
      ...ruleData,
      lineLevel: {
        ...ruleData.lineLevel,
        [field]: value
      }
    });
  };
  const handleMultiCurrencyChange = (field: string, value: any) => {
    setRuleData({
      ...ruleData,
      multiCurrency: {
        ...ruleData.multiCurrency,
        [field]: value
      }
    });
  };
  const addDefaultApprover = () => {
    const newApprover = {
      id: `default-${Date.now()}`,
      scopeType: 'Entity',
      scopeId: '',
      userOrRoleId: '',
      isReviewer: false,
      isApprover: true,
      fdoaLimit: 0,
      currency: 'AUD',
      belongsToCategories: []
    };
    setDefaultApprovers([...defaultApprovers, newApprover]);
  };
  const updateDefaultApprover = (index: number, field: string, value: any) => {
    const updatedApprovers = [...defaultApprovers];
    updatedApprovers[index] = {
      ...updatedApprovers[index],
      [field]: value
    };
    setDefaultApprovers(updatedApprovers);
  };
  const removeDefaultApprover = (index: number) => {
    const updatedApprovers = [...defaultApprovers];
    updatedApprovers.splice(index, 1);
    setDefaultApprovers(updatedApprovers);
  };
  const addCategoryApprover = () => {
    const newApprover = {
      id: `catapp-${Date.now()}`,
      categoryId: '',
      sequence: categoryApprovers.length + 1,
      userOrRoleId: '',
      limit: 0,
      currency: 'AUD'
    };
    setCategoryApprovers([...categoryApprovers, newApprover]);
  };
  const updateCategoryApprover = (index: number, field: string, value: any) => {
    const updatedApprovers = [...categoryApprovers];
    updatedApprovers[index] = {
      ...updatedApprovers[index],
      [field]: value
    };
    setCategoryApprovers(updatedApprovers);
  };
  const removeCategoryApprover = (index: number) => {
    const updatedApprovers = [...categoryApprovers];
    updatedApprovers.splice(index, 1);
    setCategoryApprovers(updatedApprovers);
  };
  const addEmergencyApprover = (userOrRoleId: string) => {
    if (!ruleData.emergency.approvers.includes(userOrRoleId)) {
      setRuleData({
        ...ruleData,
        emergency: {
          ...ruleData.emergency,
          approvers: [...ruleData.emergency.approvers, userOrRoleId]
        }
      });
    }
  };
  const removeEmergencyApprover = (userOrRoleId: string) => {
    setRuleData({
      ...ruleData,
      emergency: {
        ...ruleData.emergency,
        approvers: ruleData.emergency.approvers.filter(id => id !== userOrRoleId)
      }
    });
  };
  const addAccountingPeriod = () => {
    const today = new Date();
    const nextMonth = new Date(today);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    const newPeriod = {
      id: `period-${Date.now()}`,
      entityId: 'entity-001',
      periodCode: `FY${today.getFullYear().toString().substr(2)}-Q${Math.floor(today.getMonth() / 3) + 1}`,
      startUtc: today.toISOString(),
      endUtc: nextMonth.toISOString(),
      isClosed: false
    };
    setAccountingPeriods([...accountingPeriods, newPeriod]);
  };
  const updateAccountingPeriod = (index: number, field: string, value: any) => {
    const updatedPeriods = [...accountingPeriods];
    updatedPeriods[index] = {
      ...updatedPeriods[index],
      [field]: value
    };
    setAccountingPeriods(updatedPeriods);
  };
  const removeAccountingPeriod = (index: number) => {
    const updatedPeriods = [...accountingPeriods];
    updatedPeriods.splice(index, 1);
    setAccountingPeriods(updatedPeriods);
  };
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const updatedRule = {
      ...rule,
      ...ruleData,
      defaultApprovers,
      categoryApprovers,
      accountingPeriods
    };
    onSave(updatedRule);
  };
  // Render the form
  return <form onSubmit={handleSubmit} className="space-y-6" data-id="element-11021">
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-11022">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex justify-between items-center" data-id="element-11023">
          <div data-id="element-11024">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-11025">
              Financial Delegation Of Authority (FDOA) Rule Configuration
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-11026">
              Configure approval pathways based on financial delegation limits
              and cost centers
            </p>
          </div>
          <div className="flex space-x-3" data-id="element-11027">
            <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11028">
              Cancel
            </button>
            <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11029">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-11030" />
              Save Rule
            </button>
          </div>
        </div>
        {/* Basic Information */}
        <div className="px-4 py-5 sm:p-6 border-b border-gray-200" data-id="element-11031">
          <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-11032">
            Basic Information
          </h4>
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-11033">
            <div className="sm:col-span-3" data-id="element-11034">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-11035">
                Rule Name
              </label>
              <div className="mt-1" data-id="element-11036">
                <input type="text" id="name" value={ruleData.name} onChange={e => setRuleData({
                ...ruleData,
                name: e.target.value
              })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-11037" />
              </div>
            </div>
            <div className="sm:col-span-3" data-id="element-11038">
              <label htmlFor="version" className="block text-sm font-medium text-gray-700" data-id="element-11039">
                Version
              </label>
              <div className="mt-1" data-id="element-11040">
                <input type="text" id="version" value={ruleData.version} onChange={e => setRuleData({
                ...ruleData,
                version: e.target.value
              })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-11041" />
              </div>
            </div>
            <div className="sm:col-span-6" data-id="element-11042">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-11043">
                Description
              </label>
              <div className="mt-1" data-id="element-11044">
                <textarea id="description" rows={2} value={ruleData.description} onChange={e => setRuleData({
                ...ruleData,
                description: e.target.value
              })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-11045" />
              </div>
            </div>
            <div className="sm:col-span-3" data-id="element-11046">
              <label htmlFor="status" className="block text-sm font-medium text-gray-700" data-id="element-11047">
                Status
              </label>
              <div className="mt-1" data-id="element-11048">
                <select id="status" value={ruleData.status} onChange={e => setRuleData({
                ...ruleData,
                status: e.target.value
              })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-11049">
                  <option value="Draft" data-id="element-11050">Draft</option>
                  <option value="Active" data-id="element-11051">Active</option>
                  <option value="Inactive" data-id="element-11052">Inactive</option>
                </select>
              </div>
            </div>
            <div className="sm:col-span-3" data-id="element-11053">
              <label htmlFor="scope" className="block text-sm font-medium text-gray-700" data-id="element-11054">
                Scope
              </label>
              <div className="mt-1" data-id="element-11055">
                <select id="scope" value={ruleData.scope} onChange={e => setRuleData({
                ...ruleData,
                scope: e.target.value
              })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-11056">
                  <option value="Entity" data-id="element-11057">Entity</option>
                  <option value="Global" data-id="element-11058">Global</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        {/* Default Approvers Section */}
        <div className="border-b border-gray-200" data-id="element-11059">
          <button type="button" onClick={() => toggleSection('defaults')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-11060">
            <div className="flex items-center" data-id="element-11061">
              <LayersIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-11062" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-11063">
                Default Approvers (Vendor/Site/Entity)
              </h3>
            </div>
            {expandedSections.defaults ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-11064" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-11065" />}
          </button>
          {expandedSections.defaults && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-11066">
              <p className="text-sm text-gray-500 mb-4" data-id="element-11067">
                Configure default approvers for vendors, sites, and entities.
                These can be reviewers, approvers, or both.
              </p>
              <div className="space-y-4" data-id="element-11068">
                {defaultApprovers.map((approver, index) => <div key={approver.id} className="border border-gray-200 rounded-md p-4" data-id="element-11069">
                    <div className="flex justify-between items-start" data-id="element-11070">
                      <div className="flex items-center" data-id="element-11071">
                        {approver.scopeType === 'Entity' ? <BuildingIcon className="h-5 w-5 text-blue-500 mr-2" data-id="element-11072" /> : approver.scopeType === 'Site' ? <HomeIcon className="h-5 w-5 text-green-500 mr-2" data-id="element-11073" /> : <TagIcon className="h-5 w-5 text-orange-500 mr-2" data-id="element-11074" />}
                        <h4 className="text-sm font-medium text-gray-900" data-id="element-11075">
                          {approver.scopeType} Default Approver
                        </h4>
                      </div>
                      <button type="button" onClick={() => removeDefaultApprover(index)} className="text-red-600 hover:text-red-900" data-id="element-11076">
                        <TrashIcon className="h-4 w-4" data-id="element-11077" />
                      </button>
                    </div>
                    <div className="mt-4 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-11078">
                      <div className="sm:col-span-2" data-id="element-11079">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-11080">
                          Scope Type
                        </label>
                        <select value={approver.scopeType} onChange={e => updateDefaultApprover(index, 'scopeType', e.target.value)} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm" data-id="element-11081">
                          <option value="Entity" data-id="element-11082">Entity</option>
                          <option value="Site" data-id="element-11083">Site</option>
                          <option value="Vendor" data-id="element-11084">Vendor</option>
                        </select>
                      </div>
                      <div className="sm:col-span-4" data-id="element-11085">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-11086">
                          {approver.scopeType} Name
                        </label>
                        <select value={approver.scopeId} onChange={e => updateDefaultApprover(index, 'scopeId', e.target.value)} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm" data-id="element-11087">
                          <option value="" data-id="element-11088">Select {approver.scopeType}</option>
                          {approver.scopeType === 'Entity' && entities.map(entity => <option key={entity.id} value={entity.id} data-id="element-11089">
                                {entity.name}
                              </option>)}
                          {/* Add similar options for Site and Vendor when available */}
                        </select>
                      </div>
                      <div className="sm:col-span-3" data-id="element-11090">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-11091">
                          User or Role
                        </label>
                        <select value={approver.userOrRoleId} onChange={e => updateDefaultApprover(index, 'userOrRoleId', e.target.value)} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm" data-id="element-11092">
                          <option value="" data-id="element-11093">Select User or Role</option>
                          <optgroup label="Users" data-id="element-11094">
                            {users.map(user => <option key={user.id} value={user.id} data-id="element-11095">
                                {user.name}
                              </option>)}
                          </optgroup>
                          <optgroup label="Roles" data-id="element-11096">
                            {roles.map(role => <option key={role.id} value={role.id} data-id="element-11097">
                                {role.name}
                              </option>)}
                          </optgroup>
                        </select>
                      </div>
                      <div className="sm:col-span-3" data-id="element-11098">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-11099">
                          Approval Type
                        </label>
                        <div className="mt-2 flex items-center space-x-4" data-id="element-11100">
                          <div className="flex items-center" data-id="element-11101">
                            <input id={`reviewer-${approver.id}`} type="checkbox" checked={approver.isReviewer} onChange={e => updateDefaultApprover(index, 'isReviewer', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-11102" />
                            <label htmlFor={`reviewer-${approver.id}`} className="ml-2 block text-sm text-gray-700" data-id="element-11103">
                              Reviewer
                            </label>
                          </div>
                          <div className="flex items-center" data-id="element-11104">
                            <input id={`approver-${approver.id}`} type="checkbox" checked={approver.isApprover} onChange={e => updateDefaultApprover(index, 'isApprover', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-11105" />
                            <label htmlFor={`approver-${approver.id}`} className="ml-2 block text-sm text-gray-700" data-id="element-11106">
                              Approver
                            </label>
                          </div>
                        </div>
                      </div>
                      {approver.isApprover && <>
                          <div className="sm:col-span-2" data-id="element-11107">
                            <label className="block text-xs font-medium text-gray-700" data-id="element-11108">
                              FDOA Limit
                            </label>
                            <div className="mt-1 relative rounded-md shadow-sm" data-id="element-11109">
                              <input type="number" value={approver.fdoaLimit} onChange={e => updateDefaultApprover(index, 'fdoaLimit', parseFloat(e.target.value))} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-3 pr-12 sm:text-sm border-gray-300 rounded-md" data-id="element-11110" />
                              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-11111">
                                <span className="text-gray-500 sm:text-sm" data-id="element-11112">
                                  {approver.currency}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="sm:col-span-2" data-id="element-11113">
                            <label className="block text-xs font-medium text-gray-700" data-id="element-11114">
                              Currency
                            </label>
                            <select value={approver.currency} onChange={e => updateDefaultApprover(index, 'currency', e.target.value)} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm" data-id="element-11115">
                              {currencies.map(currency => <option key={currency.code} value={currency.code} data-id="element-11116">
                                  {currency.code}
                                </option>)}
                            </select>
                          </div>
                        </>}
                      <div className="sm:col-span-6" data-id="element-11117">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-11118">
                          Belongs to Categories (Optional)
                        </label>
                        <select multiple value={approver.belongsToCategories} onChange={e => {
                    const selected = Array.from(e.target.selectedOptions, option => option.value);
                    updateDefaultApprover(index, 'belongsToCategories', selected);
                  }} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm" size={3} data-id="element-11119">
                          {glCategories.map(category => <option key={category.id} value={category.id} data-id="element-11120">
                              {category.code} - {category.description}
                            </option>)}
                        </select>
                        <p className="mt-1 text-xs text-gray-500" data-id="element-11121">
                          Hold Ctrl/Cmd to select multiple categories
                        </p>
                      </div>
                    </div>
                  </div>)}
                <button type="button" onClick={addDefaultApprover} className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11122">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-11123" />
                  Add Default Approver
                </button>
              </div>
            </div>}
        </div>
        {/* Modes Section */}
        <div className="border-b border-gray-200" data-id="element-11124">
          <button type="button" onClick={() => toggleSection('modes')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-11125">
            <div className="flex items-center" data-id="element-11126">
              <GitBranchIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-11127" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-11128">
                Approval Modes
              </h3>
            </div>
            {expandedSections.modes ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-11129" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-11130" />}
          </button>
          {expandedSections.modes && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-11131">
              <p className="text-sm text-gray-500 mb-4" data-id="element-11132">
                Configure how default approvers interact with cost center
                approvers
              </p>
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-3" data-id="element-11133">
                <div data-id="element-11134">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-11135">
                    Vendor Mode
                  </label>
                  <div className="space-y-2" data-id="element-11136">
                    <div className="flex items-center" data-id="element-11137">
                      <input id="vendor-override" type="radio" checked={ruleData.modes.vendorMode === 'Override'} onChange={() => handleModeChange('vendorMode', 'Override')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-11138" />
                      <label htmlFor="vendor-override" className="ml-3 block text-sm text-gray-700" data-id="element-11139">
                        Override
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-11140">
                      <input id="vendor-satisfy" type="radio" checked={ruleData.modes.vendorMode === 'Satisfy'} onChange={() => handleModeChange('vendorMode', 'Satisfy')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-11141" />
                      <label htmlFor="vendor-satisfy" className="ml-3 block text-sm text-gray-700" data-id="element-11142">
                        Satisfy
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-11143">
                      <input id="vendor-costcentre" type="radio" checked={ruleData.modes.vendorMode === 'CostCentreOnly'} onChange={() => handleModeChange('vendorMode', 'CostCentreOnly')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-11144" />
                      <label htmlFor="vendor-costcentre" className="ml-3 block text-sm text-gray-700" data-id="element-11145">
                        Cost Centre Only
                      </label>
                    </div>
                  </div>
                </div>
                <div data-id="element-11146">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-11147">
                    Site Mode
                  </label>
                  <div className="space-y-2" data-id="element-11148">
                    <div className="flex items-center" data-id="element-11149">
                      <input id="site-override" type="radio" checked={ruleData.modes.siteMode === 'Override'} onChange={() => handleModeChange('siteMode', 'Override')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-11150" />
                      <label htmlFor="site-override" className="ml-3 block text-sm text-gray-700" data-id="element-11151">
                        Override
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-11152">
                      <input id="site-satisfy" type="radio" checked={ruleData.modes.siteMode === 'Satisfy'} onChange={() => handleModeChange('siteMode', 'Satisfy')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-11153" />
                      <label htmlFor="site-satisfy" className="ml-3 block text-sm text-gray-700" data-id="element-11154">
                        Satisfy
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-11155">
                      <input id="site-costcentre" type="radio" checked={ruleData.modes.siteMode === 'CostCentreOnly'} onChange={() => handleModeChange('siteMode', 'CostCentreOnly')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-11156" />
                      <label htmlFor="site-costcentre" className="ml-3 block text-sm text-gray-700" data-id="element-11157">
                        Cost Centre Only
                      </label>
                    </div>
                  </div>
                </div>
                <div data-id="element-11158">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-11159">
                    Entity Mode
                  </label>
                  <div className="space-y-2" data-id="element-11160">
                    <div className="flex items-center" data-id="element-11161">
                      <input id="entity-override" type="radio" checked={ruleData.modes.entityMode === 'Override'} onChange={() => handleModeChange('entityMode', 'Override')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-11162" />
                      <label htmlFor="entity-override" className="ml-3 block text-sm text-gray-700" data-id="element-11163">
                        Override
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-11164">
                      <input id="entity-satisfy" type="radio" checked={ruleData.modes.entityMode === 'Satisfy'} onChange={() => handleModeChange('entityMode', 'Satisfy')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-11165" />
                      <label htmlFor="entity-satisfy" className="ml-3 block text-sm text-gray-700" data-id="element-11166">
                        Satisfy
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-11167">
                      <input id="entity-costcentre" type="radio" checked={ruleData.modes.entityMode === 'CostCentreOnly'} onChange={() => handleModeChange('entityMode', 'CostCentreOnly')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-11168" />
                      <label htmlFor="entity-costcentre" className="ml-3 block text-sm text-gray-700" data-id="element-11169">
                        Cost Centre Only
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-6 bg-gray-50 p-4 rounded-md" data-id="element-11170">
                <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-11171">
                  Mode Explanations:
                </h5>
                <ul className="text-xs text-gray-600 space-y-2" data-id="element-11172">
                  <li data-id="element-11173">
                    <span className="font-semibold" data-id="element-11174">Override:</span> Default
                    approvers at this level will replace cost centre approvers
                  </li>
                  <li data-id="element-11175">
                    <span className="font-semibold" data-id="element-11176">Satisfy:</span> Default
                    approvers count as part of category sequence if they belong
                    to the category
                  </li>
                  <li data-id="element-11177">
                    <span className="font-semibold" data-id="element-11178">Cost Centre Only:</span>{' '}
                    Ignore default approvers at this level, use only cost centre
                    approvers
                  </li>
                </ul>
              </div>
            </div>}
        </div>
        {/* Delegation & Escalation Section */}
        <div className="border-b border-gray-200" data-id="element-11179">
          <button type="button" onClick={() => toggleSection('delegation')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-11180">
            <div className="flex items-center" data-id="element-11181">
              <ArrowUpRightIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-11182" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-11183">
                Delegation & Escalation
              </h3>
            </div>
            {expandedSections.delegation ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-11184" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-11185" />}
          </button>
          {expandedSections.delegation && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-11186">
              <p className="text-sm text-gray-500 mb-4" data-id="element-11187">
                Configure delegation and escalation settings
              </p>
              <div className="space-y-4" data-id="element-11188">
                <div className="flex items-center" data-id="element-11189">
                  <input id="inherit-limit" type="checkbox" checked={ruleData.delegation.inheritUpperLimit} onChange={e => handleDelegationChange('inheritUpperLimit', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-11190" />
                  <label htmlFor="inherit-limit" className="ml-3 block text-sm text-gray-700" data-id="element-11191">
                    Delegate inherits original approver's upper limit
                  </label>
                  <HelpCircleIcon className="h-4 w-4 text-gray-400 ml-2" title="If enabled, a delegate will use the original approver's FDOA limit instead of their own" data-id="element-11192" />
                </div>
                <div className="flex items-center" data-id="element-11193">
                  <input id="original-chain" type="checkbox" checked={ruleData.delegation.useOriginalChain} onChange={e => handleDelegationChange('useOriginalChain', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-11194" />
                  <label htmlFor="original-chain" className="ml-3 block text-sm text-gray-700" data-id="element-11195">
                    Escalate along original approver's manager chain
                  </label>
                  <HelpCircleIcon className="h-4 w-4 text-gray-400 ml-2" title="If enabled, escalation will follow the original approver's management chain rather than the delegate's" data-id="element-11196" />
                </div>
                <div data-id="element-11197">
                  <label htmlFor="max-hops" className="block text-sm font-medium text-gray-700" data-id="element-11198">
                    Maximum Escalation Hops
                  </label>
                  <div className="mt-1 w-24" data-id="element-11199">
                    <input type="number" id="max-hops" min="1" max="10" value={ruleData.delegation.maxHops} onChange={e => handleDelegationChange('maxHops', parseInt(e.target.value))} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-11200" />
                  </div>
                  <p className="mt-1 text-xs text-gray-500" data-id="element-11201">
                    Maximum number of manager levels to escalate through
                  </p>
                </div>
              </div>
            </div>}
        </div>
        {/* Category Approvers Section */}
        <div className="border-b border-gray-200" data-id="element-11202">
          <button type="button" onClick={() => toggleSection('categories')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-11203">
            <div className="flex items-center" data-id="element-11204">
              <LayersIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-11205" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-11206">
                Category Approvers
              </h3>
            </div>
            {expandedSections.categories ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-11207" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-11208" />}
          </button>
          {expandedSections.categories && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-11209">
              <p className="text-sm text-gray-500 mb-4" data-id="element-11210">
                Configure approvers for each GL category (cost centre)
              </p>
              <div className="space-y-4" data-id="element-11211">
                {categoryApprovers.map((approver, index) => <div key={approver.id} className="border border-gray-200 rounded-md p-4" data-id="element-11212">
                    <div className="flex justify-between items-start" data-id="element-11213">
                      <div className="flex items-center" data-id="element-11214">
                        <LayersIcon className="h-5 w-5 text-blue-500 mr-2" data-id="element-11215" />
                        <h4 className="text-sm font-medium text-gray-900" data-id="element-11216">
                          Category Approver (Sequence {approver.sequence})
                        </h4>
                      </div>
                      <button type="button" onClick={() => removeCategoryApprover(index)} className="text-red-600 hover:text-red-900" data-id="element-11217">
                        <TrashIcon className="h-4 w-4" data-id="element-11218" />
                      </button>
                    </div>
                    <div className="mt-4 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-11219">
                      <div className="sm:col-span-3" data-id="element-11220">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-11221">
                          GL Category
                        </label>
                        <select value={approver.categoryId} onChange={e => updateCategoryApprover(index, 'categoryId', e.target.value)} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm" data-id="element-11222">
                          <option value="" data-id="element-11223">Select GL Category</option>
                          {glCategories.map(category => <option key={category.id} value={category.id} data-id="element-11224">
                              {category.code} - {category.description}
                            </option>)}
                        </select>
                      </div>
                      <div className="sm:col-span-1" data-id="element-11225">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-11226">
                          Sequence
                        </label>
                        <input type="number" min="1" value={approver.sequence} onChange={e => updateCategoryApprover(index, 'sequence', parseInt(e.target.value))} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm" data-id="element-11227" />
                      </div>
                      <div className="sm:col-span-2" data-id="element-11228">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-11229">
                          User or Role
                        </label>
                        <select value={approver.userOrRoleId} onChange={e => updateCategoryApprover(index, 'userOrRoleId', e.target.value)} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm" data-id="element-11230">
                          <option value="" data-id="element-11231">Select User or Role</option>
                          <optgroup label="Users" data-id="element-11232">
                            {users.map(user => <option key={user.id} value={user.id} data-id="element-11233">
                                {user.name}
                              </option>)}
                          </optgroup>
                          <optgroup label="Roles" data-id="element-11234">
                            {roles.map(role => <option key={role.id} value={role.id} data-id="element-11235">
                                {role.name}
                              </option>)}
                          </optgroup>
                        </select>
                      </div>
                      <div className="sm:col-span-2" data-id="element-11236">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-11237">
                          Approval Limit
                        </label>
                        <div className="mt-1 flex rounded-md shadow-sm" data-id="element-11238">
                          <input type="number" value={approver.limit} onChange={e => updateCategoryApprover(index, 'limit', parseFloat(e.target.value))} className="focus:ring-indigo-500 focus:border-indigo-500 flex-1 block w-full rounded-none rounded-l-md sm:text-sm border-gray-300" data-id="element-11239" />
                          <select value={approver.currency} onChange={e => updateCategoryApprover(index, 'currency', e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm" data-id="element-11240">
                            {currencies.map(currency => <option key={currency.code} value={currency.code} data-id="element-11241">
                                {currency.code}
                              </option>)}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>)}
                <button type="button" onClick={addCategoryApprover} className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11242">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-11243" />
                  Add Category Approver
                </button>
              </div>
            </div>}
        </div>
        {/* Four-Eyes Section */}
        <div className="border-b border-gray-200" data-id="element-11244">
          <button type="button" onClick={() => toggleSection('fourEyes')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-11245">
            <div className="flex items-center" data-id="element-11246">
              <EyeIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-11247" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-11248">
                Four-Eyes Principle
              </h3>
            </div>
            {expandedSections.fourEyes ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-11249" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-11250" />}
          </button>
          {expandedSections.fourEyes && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-11251">
              <p className="text-sm text-gray-500 mb-4" data-id="element-11252">
                Configure Four-Eyes principle requiring two distinct approvers
              </p>
              <div className="flex items-center mb-4" data-id="element-11253">
                <input id="enable-four-eyes" type="checkbox" checked={ruleData.fourEyes.enabled} onChange={e => handleFourEyesChange('enabled', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-11254" />
                <label htmlFor="enable-four-eyes" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-11255">
                  Enable Four-Eyes Principle
                </label>
              </div>
              {ruleData.fourEyes.enabled && <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-3" data-id="element-11256">
                  <div data-id="element-11257">
                    <label htmlFor="four-eyes-threshold" className="block text-sm font-medium text-gray-700" data-id="element-11258">
                      Threshold
                    </label>
                    <div className="mt-1 flex rounded-md shadow-sm" data-id="element-11259">
                      <input type="number" id="four-eyes-threshold" value={ruleData.fourEyes.threshold} onChange={e => handleFourEyesChange('threshold', parseFloat(e.target.value))} className="focus:ring-indigo-500 focus:border-indigo-500 flex-1 block w-full rounded-none rounded-l-md sm:text-sm border-gray-300" data-id="element-11260" />
                      <select value={ruleData.fourEyes.currency} onChange={e => handleFourEyesChange('currency', e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm" data-id="element-11261">
                        {currencies.map(currency => <option key={currency.code} value={currency.code} data-id="element-11262">
                            {currency.code}
                          </option>)}
                      </select>
                    </div>
                    <p className="mt-1 text-xs text-gray-500" data-id="element-11263">
                      Four-Eyes principle applies to transactions above this
                      amount
                    </p>
                  </div>
                  <div data-id="element-11264">
                    <label htmlFor="four-eyes-apply" className="block text-sm font-medium text-gray-700" data-id="element-11265">
                      Apply At
                    </label>
                    <select id="four-eyes-apply" value={ruleData.fourEyes.applyAt} onChange={e => handleFourEyesChange('applyAt', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-11266">
                      <option value="Header" data-id="element-11267">Header</option>
                      <option value="Line" data-id="element-11268">Line</option>
                      <option value="Both" data-id="element-11269">Both</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500" data-id="element-11270">
                      Where to apply the Four-Eyes check
                    </p>
                  </div>
                </div>}
              <div className="mt-4 bg-yellow-50 p-4 rounded-md" data-id="element-11271">
                <div className="flex" data-id="element-11272">
                  <div className="flex-shrink-0" data-id="element-11273">
                    <AlertTriangleIcon className="h-5 w-5 text-yellow-400" data-id="element-11274" />
                  </div>
                  <div className="ml-3" data-id="element-11275">
                    <h3 className="text-sm font-medium text-yellow-800" data-id="element-11276">
                      Four-Eyes Principle Note
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700" data-id="element-11277">
                      <p data-id="element-11278">When enabled, Four-Eyes requires that:</p>
                      <ul className="list-disc pl-5 mt-1 space-y-1" data-id="element-11279">
                        <li data-id="element-11280">
                          Two distinct approvers must approve transactions above
                          the threshold
                        </li>
                        <li data-id="element-11281">
                          The requester/coder of a document cannot be one of its
                          approvers
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
        </div>
        {/* Emergency Approvals Section */}
        <div className="border-b border-gray-200" data-id="element-11282">
          <button type="button" onClick={() => toggleSection('emergency')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-11283">
            <div className="flex items-center" data-id="element-11284">
              <AlertTriangleIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-11285" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-11286">
                Emergency Approvals
              </h3>
            </div>
            {expandedSections.emergency ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-11287" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-11288" />}
          </button>
          {expandedSections.emergency && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-11289">
              <p className="text-sm text-gray-500 mb-4" data-id="element-11290">
                Configure emergency approvers who can short-circuit normal
                approval flows
              </p>
              <div className="flex items-center mb-4" data-id="element-11291">
                <input id="enable-emergency" type="checkbox" checked={ruleData.emergency.enabled} onChange={e => handleEmergencyChange('enabled', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-11292" />
                <label htmlFor="enable-emergency" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-11293">
                  Enable Emergency Approvals
                </label>
              </div>
              {ruleData.emergency.enabled && <div data-id="element-11294">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-11295">
                    Emergency Approvers
                  </label>
                  <div className="mb-4" data-id="element-11296">
                    <select id="emergency-approver-select" onChange={e => {
                if (e.target.value) {
                  addEmergencyApprover(e.target.value);
                  e.target.value = '';
                }
              }} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-11297">
                      <option value="" data-id="element-11298">Add Emergency Approver</option>
                      <optgroup label="Users" data-id="element-11299">
                        {users.filter(user => !ruleData.emergency.approvers.includes(user.id)).map(user => <option key={user.id} value={user.id} data-id="element-11300">
                              {user.name}
                            </option>)}
                      </optgroup>
                      <optgroup label="Roles" data-id="element-11301">
                        {roles.filter(role => !ruleData.emergency.approvers.includes(role.id)).map(role => <option key={role.id} value={role.id} data-id="element-11302">
                              {role.name}
                            </option>)}
                      </optgroup>
                    </select>
                  </div>
                  {ruleData.emergency.approvers.length > 0 ? <ul className="divide-y divide-gray-200 border border-gray-200 rounded-md" data-id="element-11303">
                      {ruleData.emergency.approvers.map(approverId => {
                const user = users.find(u => u.id === approverId);
                const role = roles.find(r => r.id === approverId);
                const name = user?.name || role?.name || 'Unknown';
                const isUser = !!user;
                return <li key={approverId} className="px-4 py-3 flex items-center justify-between" data-id="element-11304">
                            <div className="flex items-center" data-id="element-11305">
                              {isUser ? <UserIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-11306" /> : <UsersIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-11307" />}
                              <span className="text-sm text-gray-900" data-id="element-11308">
                                {name}
                              </span>
                            </div>
                            <button type="button" onClick={() => removeEmergencyApprover(approverId)} className="text-red-600 hover:text-red-900" data-id="element-11309">
                              <TrashIcon className="h-4 w-4" data-id="element-11310" />
                            </button>
                          </li>;
              })}
                    </ul> : <div className="text-sm text-gray-500 italic" data-id="element-11311">
                      No emergency approvers selected
                    </div>}
                  <div className="mt-4 bg-yellow-50 p-4 rounded-md" data-id="element-11312">
                    <div className="flex" data-id="element-11313">
                      <div className="flex-shrink-0" data-id="element-11314">
                        <AlertTriangleIcon className="h-5 w-5 text-yellow-400" data-id="element-11315" />
                      </div>
                      <div className="ml-3" data-id="element-11316">
                        <h3 className="text-sm font-medium text-yellow-800" data-id="element-11317">
                          Emergency Approval Note
                        </h3>
                        <div className="mt-2 text-sm text-yellow-700" data-id="element-11318">
                          <p data-id="element-11319">
                            Emergency approvers can short-circuit normal
                            approval flows in urgent situations. The Four-Eyes
                            principle will still be enforced if enabled.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>}
            </div>}
        </div>
        {/* Line-Level Evaluation Section */}
        <div className="border-b border-gray-200" data-id="element-11320">
          <button type="button" onClick={() => toggleSection('lineLevel')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-11321">
            <div className="flex items-center" data-id="element-11322">
              <LayersIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-11323" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-11324">
                Line-Level Evaluation
              </h3>
            </div>
            {expandedSections.lineLevel ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-11325" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-11326" />}
          </button>
          {expandedSections.lineLevel && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-11327">
              <p className="text-sm text-gray-500 mb-4" data-id="element-11328">
                Configure line-level and multi-cost-centre evaluation
              </p>
              <div className="space-y-4" data-id="element-11329">
                <div className="flex items-center" data-id="element-11330">
                  <input id="enable-line-level" type="checkbox" checked={ruleData.lineLevel.enabled} onChange={e => handleLineLevelChange('enabled', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-11331" />
                  <label htmlFor="enable-line-level" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-11332">
                    Enable Line-Level Evaluation
                  </label>
                </div>
                {ruleData.lineLevel.enabled && <>
                    <div data-id="element-11333">
                      <label htmlFor="evaluation-mode" className="block text-sm font-medium text-gray-700" data-id="element-11334">
                        Evaluation Mode
                      </label>
                      <select id="evaluation-mode" value={ruleData.lineLevel.evaluationMode} onChange={e => handleLineLevelChange('evaluationMode', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-11335">
                        <option value="Sequential" data-id="element-11336">Sequential</option>
                        <option value="Parallel" data-id="element-11337">Parallel</option>
                      </select>
                      <p className="mt-1 text-xs text-gray-500" data-id="element-11338">
                        Sequential: Lines are approved in sequence. Parallel:
                        All lines can be approved simultaneously.
                      </p>
                    </div>
                    <div className="flex items-center" data-id="element-11339">
                      <input id="categories-only" type="checkbox" checked={ruleData.lineLevel.categoriesOnly} onChange={e => handleLineLevelChange('categoriesOnly', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-11340" />
                      <label htmlFor="categories-only" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-11341">
                        Approvers only for categories they're authorised for
                      </label>
                      <HelpCircleIcon className="h-4 w-4 text-gray-400 ml-2" title="When enabled, approvers will only see and approve lines for GL categories they are authorized for" data-id="element-11342" />
                    </div>
                  </>}
                <div className="mt-4 bg-gray-50 p-4 rounded-md" data-id="element-11343">
                  <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-11344">
                    Line-Level Evaluation:
                  </h5>
                  <ul className="text-xs text-gray-600 space-y-2" data-id="element-11345">
                    <li data-id="element-11346">
                      Evaluates each line item separately based on its GL
                      code/cost centre
                    </li>
                    <li data-id="element-11347">
                      Allows for different approvers for different types of
                      expenses
                    </li>
                    <li data-id="element-11348">Can handle documents with multiple cost centres</li>
                  </ul>
                </div>
              </div>
            </div>}
        </div>
        {/* Accounting Periods Section */}
        <div className="border-b border-gray-200" data-id="element-11349">
          <button type="button" onClick={() => toggleSection('periods')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-11350">
            <div className="flex items-center" data-id="element-11351">
              <CalendarIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-11352" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-11353">
                Accounting Periods
              </h3>
            </div>
            {expandedSections.periods ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-11354" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-11355" />}
          </button>
          {expandedSections.periods && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-11356">
              <p className="text-sm text-gray-500 mb-4" data-id="element-11357">
                Configure accounting periods and closed period checks
              </p>
              <div className="flex items-center mb-4" data-id="element-11358">
                <input id="check-closed-periods" type="checkbox" checked={ruleData.preApproval.checkClosedPeriods} onChange={e => setRuleData({
              ...ruleData,
              preApproval: {
                ...ruleData.preApproval,
                checkClosedPeriods: e.target.checked
              }
            })} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-11359" />
                <label htmlFor="check-closed-periods" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-11360">
                  Check for closed accounting periods
                </label>
              </div>
              {ruleData.preApproval.checkClosedPeriods && <div className="space-y-4" data-id="element-11361">
                  <div className="overflow-x-auto" data-id="element-11362">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-11363">
                      <thead className="bg-gray-50" data-id="element-11364">
                        <tr data-id="element-11365">
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11366">
                            Entity
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11367">
                            Period Code
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11368">
                            Start Date
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11369">
                            End Date
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11370">
                            Status
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11371">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-11372">
                        {accountingPeriods.map((period, index) => {
                    const entity = entities.find(e => e.id === period.entityId);
                    return <tr key={period.id} data-id="element-11373">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-11374">
                                {entity?.name || 'Unknown Entity'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-11375">
                                {period.periodCode}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-11376">
                                {new Date(period.startUtc).toLocaleDateString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-11377">
                                {new Date(period.endUtc).toLocaleDateString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap" data-id="element-11378">
                                {period.isClosed ? <StatusBadge status="error" text="Closed" data-id="element-11379" /> : <StatusBadge status="success" text="Open" data-id="element-11380" />}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-11381">
                                <div className="flex justify-end space-x-2" data-id="element-11382">
                                  <button type="button" onClick={() => updateAccountingPeriod(index, 'isClosed', !period.isClosed)} className={period.isClosed ? 'text-green-600 hover:text-green-900' : 'text-amber-600 hover:text-amber-900'} title={period.isClosed ? 'Open Period' : 'Close Period'} data-id="element-11383">
                                    {period.isClosed ? <CheckIcon className="h-4 w-4" data-id="element-11384" /> : <LockIcon className="h-4 w-4" data-id="element-11385" />}
                                  </button>
                                  <button type="button" onClick={() => removeAccountingPeriod(index)} className="text-red-600 hover:text-red-900" title="Remove Period" data-id="element-11386">
                                    <TrashIcon className="h-4 w-4" data-id="element-11387" />
                                  </button>
                                </div>
                              </td>
                            </tr>;
                  })}
                      </tbody>
                    </table>
                  </div>
                  <button type="button" onClick={addAccountingPeriod} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11388">
                    <PlusIcon className="h-4 w-4 mr-2" data-id="element-11389" />
                    Add Accounting Period
                  </button>
                </div>}
            </div>}
        </div>
        {/* Multi-Currency Section */}
        <div className="border-b border-gray-200" data-id="element-11390">
          <button type="button" onClick={() => toggleSection('multiCurrency')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-11391">
            <div className="flex items-center" data-id="element-11392">
              <DollarSignIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-11393" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-11394">
                Multi-Currency
              </h3>
            </div>
            {expandedSections.multiCurrency ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-11395" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-11396" />}
          </button>
          {expandedSections.multiCurrency && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-11397">
              <p className="text-sm text-gray-500 mb-4" data-id="element-11398">
                Configure multi-currency support
              </p>
              <div className="space-y-4" data-id="element-11399">
                <div className="flex items-center" data-id="element-11400">
                  <input id="enable-multi-currency" type="checkbox" checked={ruleData.multiCurrency.enabled} onChange={e => handleMultiCurrencyChange('enabled', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-11401" />
                  <label htmlFor="enable-multi-currency" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-11402">
                    Enable Multi-Currency Support
                  </label>
                </div>
                {ruleData.multiCurrency.enabled && <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-3" data-id="element-11403">
                    <div data-id="element-11404">
                      <label htmlFor="base-currency" className="block text-sm font-medium text-gray-700" data-id="element-11405">
                        Base Currency
                      </label>
                      <select id="base-currency" value={ruleData.multiCurrency.baseCurrency} onChange={e => handleMultiCurrencyChange('baseCurrency', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-11406">
                        {currencies.map(currency => <option key={currency.code} value={currency.code} data-id="element-11407">
                            {currency.code} - {currency.name}
                          </option>)}
                      </select>
                    </div>
                    <div data-id="element-11408">
                      <label htmlFor="evaluate-at" className="block text-sm font-medium text-gray-700" data-id="element-11409">
                        Evaluate Thresholds At
                      </label>
                      <select id="evaluate-at" value={ruleData.multiCurrency.evaluateAt} onChange={e => handleMultiCurrencyChange('evaluateAt', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-11410">
                        <option value="Header" data-id="element-11411">Header (Document Total)</option>
                        <option value="Line" data-id="element-11412">Line (Line Subtotals)</option>
                      </select>
                    </div>
                  </div>}
                <div className="mt-4 bg-gray-50 p-4 rounded-md" data-id="element-11413">
                  <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-11414">
                    Multi-Currency Support:
                  </h5>
                  <ul className="text-xs text-gray-600 space-y-2" data-id="element-11415">
                    <li data-id="element-11416">
                      Converts transaction amounts to the base currency for
                      threshold evaluation
                    </li>
                    <li data-id="element-11417">Uses current exchange rates from the system</li>
                    <li data-id="element-11418">
                      Allows approvers to have limits in different currencies
                    </li>
                  </ul>
                </div>
              </div>
            </div>}
        </div>
        {/* Audit Section */}
        <div className="border-b border-gray-200" data-id="element-11419">
          <button type="button" onClick={() => toggleSection('audit')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-11420">
            <div className="flex items-center" data-id="element-11421">
              <ClockIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-11422" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-11423">Audit</h3>
            </div>
            {expandedSections.audit ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-11424" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-11425" />}
          </button>
          {expandedSections.audit && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-11426">
              <p className="text-sm text-gray-500 mb-4" data-id="element-11427">
                Configure audit settings
              </p>
              <div className="space-y-4" data-id="element-11428">
                <div className="flex items-center" data-id="element-11429">
                  <input id="enable-audit" type="checkbox" checked={ruleData.audit.enabled} onChange={e => setRuleData({
                ...ruleData,
                audit: {
                  ...ruleData.audit,
                  enabled: e.target.checked
                }
              })} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-11430" />
                  <label htmlFor="enable-audit" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-11431">
                    Enable Audit Logging
                  </label>
                </div>
                {ruleData.audit.enabled && <div className="flex items-center" data-id="element-11432">
                    <input id="store-decisions" type="checkbox" checked={ruleData.audit.storeDecisions} onChange={e => setRuleData({
                ...ruleData,
                audit: {
                  ...ruleData.audit,
                  storeDecisions: e.target.checked
                }
              })} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-11433" />
                    <label htmlFor="store-decisions" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-11434">
                      Store Rule Decisions
                    </label>
                    <HelpCircleIcon className="h-4 w-4 text-gray-400 ml-2" title="When enabled, all rule decisions (Approved, Escalated, Failed, etc.) will be stored in the audit log" data-id="element-11435" />
                  </div>}
                <div className="mt-4 bg-gray-50 p-4 rounded-md" data-id="element-11436">
                  <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-11437">
                    Audit Features:
                  </h5>
                  <ul className="text-xs text-gray-600 space-y-2" data-id="element-11438">
                    <li data-id="element-11439">
                      Append-only audit log ensures all rule evaluations are
                      recorded
                    </li>
                    <li data-id="element-11440">
                      Tamper-evident hashing ensures audit trail integrity
                    </li>
                    <li data-id="element-11441">
                      Compliant with ISO27001 and other regulatory requirements
                    </li>
                  </ul>
                </div>
              </div>
            </div>}
        </div>
      </div>
      <div className="flex justify-end space-x-3" data-id="element-11442">
        <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11443">
          Cancel
        </button>
        <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11444">
          <SaveIcon className="h-4 w-4 mr-2" data-id="element-11445" />
          Save Rule
        </button>
      </div>
    </form>;
}